import React, { useState, useEffect } from 'react';
import { Gift, X, Sparkles, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = '/api';

const CreditReminderBanner = ({ onBookNow }) => {
  const [creditData, setCreditData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  // Get user email from localStorage or auth context
  const userEmail = localStorage.getItem('user_email') || localStorage.getItem('userEmail');

  useEffect(() => {
    if (userEmail) {
      fetchCredits();
    } else {
      setLoading(false);
    }
  }, [userEmail]);

  const fetchCredits = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/credits/stats/${encodeURIComponent(userEmail)}`);
      setCreditData(response.data);
      
      // Check if user dismissed today
      const lastDismissed = localStorage.getItem('credit_banner_dismissed');
      if (lastDismissed) {
        const dismissedDate = new Date(lastDismissed);
        const today = new Date();
        // Show again if it's been more than 24 hours
        if ((today - dismissedDate) < 24 * 60 * 60 * 1000) {
          setDismissed(true);
        }
      }
    } catch (error) {
      console.error('Error fetching credits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('credit_banner_dismissed', new Date().toISOString());
  };

  const handleBookNow = () => {
    // Scroll to booking section
    const bookingSection = document.getElementById('booking');
    if (bookingSection) {
      bookingSection.scrollIntoView({ behavior: 'smooth' });
    }
    if (onBookNow) onBookNow();
  };

  // Don't show if loading, dismissed, no user, or no credits
  if (loading || dismissed || !userEmail || !creditData) return null;
  if (creditData.credits_available <= 0) return null;

  const freeHours = Math.floor(creditData.credits_available / 3);
  const credits = creditData.credits_available;
  const totalHours = creditData.total_hours || 0;

  // If user doesn't have enough credits for a full hour, show credits progress
  if (freeHours <= 0) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] animate-slideDown">
        <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 bg-white/20 p-2 rounded-full">
                  <Gift size={24} className="text-yellow-300" />
                </div>
                <div>
                  <p className="font-bold text-sm md:text-base flex items-center">
                    <Sparkles size={16} className="mr-1 text-yellow-300" />
                    You have {credits} credits! {3 - credits} more to earn a FREE hour!
                  </p>
                  <p className="text-xs md:text-sm text-white/80">
                    Keep booking to earn your free studio time!
                  </p>
                </div>
              </div>
              <button onClick={handleDismiss} className="p-2 hover:bg-white/20 rounded-full transition-colors" aria-label="Dismiss">
                <X size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] animate-slideDown">
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left: Icon and Message */}
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 bg-white/20 p-2 rounded-full animate-pulse">
                <Gift size={24} className="text-yellow-300" />
              </div>
              <div>
                <p className="font-bold text-sm md:text-base flex items-center">
                  <Sparkles size={16} className="mr-1 text-yellow-300" />
                  You have {freeHours} FREE HOUR{freeHours > 1 ? 'S' : ''} waiting! ({credits} credits)
                </p>
                <p className="text-xs md:text-sm text-white/80">
                  Thanks for being a loyal customer with {totalHours} hours booked!
                </p>
              </div>
            </div>

            {/* Right: CTA and Dismiss */}
            <div className="flex items-center space-x-2">
              <button
                onClick={handleBookNow}
                className="hidden sm:flex items-center bg-white text-purple-600 px-4 py-2 rounded-full font-bold text-sm hover:bg-yellow-300 hover:text-purple-700 transition-all transform hover:scale-105"
              >
                Use Now
                <ChevronRight size={18} className="ml-1" />
              </button>
              <button
                onClick={handleDismiss}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Dismiss"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile CTA Bar */}
      <div className="sm:hidden bg-gradient-to-r from-yellow-400 to-orange-500">
        <button
          onClick={handleBookNow}
          className="w-full py-2 text-purple-900 font-bold text-sm flex items-center justify-center"
        >
          Tap to use your FREE hour{freeHours > 1 ? 's' : ''}
          <ChevronRight size={18} className="ml-1" />
        </button>
      </div>
    </div>
  );
};

export default CreditReminderBanner;
